/* Hallmark · component-discipline · panel-system · theme: Cobalt-adapted
 * All colors via project CSS tokens (var(--*)). No hardcoded slate-* values.
 * Pattern: header-strip / scroll-body / footer-strip
 */
import React, { useRef, useEffect } from 'react'
import { X } from 'lucide-react'

interface FormDrawerProps {
  title: string
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  maxWidth?: number
  subtitle?: string
}

export function FormDrawer({
  title,
  isOpen,
  onClose,
  children,
  footer,
  maxWidth = 540,
  subtitle,
}: FormDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  // Keyboard trap: Escape closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handler)
      // Focus close button on open
      requestAnimationFrame(() => closeBtnRef.current?.focus())
    }
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Scrim — click to close */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(2,6,23,0.55)' }}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Drawer shell */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-drawer-title"
        className="fixed top-0 right-0 bottom-0 z-50 flex flex-col shadow-2xl"
        style={{
          width: `${maxWidth}px`,
          maxWidth: '100%',
          background: 'var(--bg-panel)',
          borderLeft: '1px solid var(--border-color)',
        }}
      >
        {/* ── Header strip ── */}
        <div
          className="shrink-0 flex items-center justify-between px-4 py-3 gap-3"
          style={{
            background: 'var(--bg-app)',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div className="min-w-0">
            <h2
              id="form-drawer-title"
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: 'var(--accent)', letterSpacing: '0.12em' }}
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>
                {subtitle}
              </p>
            )}
          </div>

          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Закрыть панель"
            className="shrink-0 p-1.5 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
            style={{
              color: 'var(--text-muted)',
              ['--tw-ring-color' as string]: 'var(--border-focus)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'
              ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'
            }}
            onMouseLeave={e => {
              ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'
              ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
            }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {children}
        </div>

        {/* ── Footer strip ── */}
        {footer && (
          <div
            className="shrink-0 flex items-center justify-end gap-2 px-4 py-3"
            style={{
              background: 'var(--bg-app)',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            {footer}
          </div>
        )}
      </aside>
    </>
  )
}
